import { useContext, useState } from 'react'
import { TelemetryDataContext } from '../context/TelemetryDataContext'
import {
  AreaChart, Area, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts'

const TABS = [
  { key: 'errors', label: 'Errors' },
  { key: 'progress', label: 'AI & Progress' },
]

const ERROR_COLORS = {
  syntax: '#FF6B6B',
  runtime: '#FFA500',
  logic: '#4169E1',
  test_failed: '#9B9B9B',
}

const FALLBACK_ERROR = [
  { time: '0m', syntax: 0, runtime: 0, logic: 0, test_failed: 0 },
]

const FALLBACK_PROGRESS = [
  { time: '0m', aiQueries: 0, cumulativeAiQueries: 0, passRate: 0 },
]

const ErrorTooltip = ({ active, payload, label }) => {
  if (!active || !payload) return null
  const total = payload.reduce((sum, entry) => sum + entry.value, 0)
  return (
    <div style={{
      background: 'white',
      border: '1px solid #e0e0e0',
      borderRadius: '6px',
      padding: '10px 14px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      fontSize: '13px',
    }}>
      <div style={{ fontWeight: 600, marginBottom: '6px', color: '#333' }}>{label}</div>
      {payload.map((entry, index) => (
        <div key={index} style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
          <span style={{ color: entry.color }}>
            {entry.name.replace('_', ' ').replace(/\b\w/g, c => c.toUpperCase())}
          </span>
          <span style={{ fontWeight: 500 }}>{entry.value}</span>
        </div>
      ))}
      <div style={{ borderTop: '1px solid #eee', marginTop: '4px', paddingTop: '4px', fontWeight: 600 }}>
        Total: {total}
      </div>
    </div>
  )
}

const ProgressTooltip = ({ active, payload, label }) => {
  if (!active || !payload) return null
  return (
    <div style={{
      background: 'white',
      border: '1px solid #e0e0e0',
      borderRadius: '6px',
      padding: '10px 14px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      fontSize: '13px',
    }}>
      <div style={{ fontWeight: 600, marginBottom: '6px', color: '#333' }}>{label}</div>
      {payload.map((entry, index) => (
        <div key={index} style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
          <span style={{ color: entry.color }}>{entry.name}</span>
          <span style={{ fontWeight: 500 }}>
            {entry.dataKey === 'passRate' ? `${entry.value}%` : entry.value}
          </span>
        </div>
      ))}
    </div>
  )
}

export default function ClassCodingTimeline() {
  const { stats } = useContext(TelemetryDataContext)
  const [activeTab, setActiveTab] = useState('errors')

  const hasErrorData = stats?.errorDistribution?.length > 0
  const errorData = hasErrorData ? stats.errorDistribution : FALLBACK_ERROR

  const hasProgressData = stats?.progressTimeline?.length > 0
  const progressData = hasProgressData ? stats.progressTimeline : FALLBACK_PROGRESS

  const tabStyle = (key) => ({
    padding: '4px 12px',
    fontSize: '12px',
    fontWeight: activeTab === key ? 600 : 400,
    color: activeTab === key ? '#0070CC' : '#888',
    background: activeTab === key ? '#e8f0fe' : 'transparent',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    transition: 'all 0.15s',
  })

  return (
    <div className="class-coding-timeline" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header with tabs */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>
          {activeTab === 'errors' ? 'Error Distribution' : 'AI Usage & Pass Rate'}
        </h2>
        <div style={{
          display: 'flex',
          gap: '4px',
          background: '#f5f5f5',
          borderRadius: '8px',
          padding: '3px',
        }}>
          {TABS.map(tab => (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={tabStyle(tab.key)}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Chart area */}
      <div style={{ flex: 1, minHeight: 0, position: 'relative', marginTop: '8px' }}>
        {activeTab === 'errors' && !hasErrorData && (
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#9ca3af', fontSize: '15px', zIndex: 1,
          }}>
            No error data yet
          </div>
        )}
        {activeTab === 'progress' && !hasProgressData && (
          <div style={{
            position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#9ca3af', fontSize: '15px', zIndex: 1,
          }}>
            No progress data yet
          </div>
        )}

        <ResponsiveContainer width="100%" height="100%">
          {activeTab === 'errors' ? (
            <AreaChart data={errorData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="time" tick={{ fontSize: 12, fill: '#666' }} axisLine={{ stroke: '#e0e0e0' }} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: '#666' }} axisLine={false} tickLine={false} />
              <Tooltip content={<ErrorTooltip />} />
              <Area type="monotone" dataKey="syntax" stackId="1" stroke={ERROR_COLORS.syntax} fill={ERROR_COLORS.syntax} fillOpacity={0.3} strokeWidth={2} />
              <Area type="monotone" dataKey="runtime" stackId="1" stroke={ERROR_COLORS.runtime} fill={ERROR_COLORS.runtime} fillOpacity={0.3} strokeWidth={2} />
              <Area type="monotone" dataKey="logic" stackId="1" stroke={ERROR_COLORS.logic} fill={ERROR_COLORS.logic} fillOpacity={0.3} strokeWidth={2} />
              <Area type="monotone" dataKey="test_failed" stackId="1" stroke={ERROR_COLORS.test_failed} fill={ERROR_COLORS.test_failed} fillOpacity={0.3} strokeWidth={2} />
            </AreaChart>
          ) : (
            <LineChart data={progressData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
              <defs>
                <linearGradient id="aiGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="passGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
              <XAxis dataKey="time" tick={{ fontSize: 12, fill: '#666' }} axisLine={{ stroke: '#e0e0e0' }} tickLine={false} />
              <YAxis yAxisId="left" tick={{ fontSize: 11, fill: '#8B5CF6' }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fontSize: 11, fill: '#10B981' }} tickFormatter={(v) => `${v}%`} axisLine={false} tickLine={false} />
              <Tooltip content={<ProgressTooltip />} />
              <Line yAxisId="left" type="monotone" dataKey="cumulativeAiQueries" name="AI Queries" stroke="#8B5CF6" strokeWidth={2.5} dot={false} activeDot={{ r: 4, fill: '#8B5CF6', stroke: 'white', strokeWidth: 2 }} />
              <Line yAxisId="right" type="monotone" dataKey="passRate" name="Pass Rate" stroke="#10B981" strokeWidth={2.5} dot={false} activeDot={{ r: 4, fill: '#10B981', stroke: 'white', strokeWidth: 2 }} />
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  )
}